import { checkWithinSeCe } from 'components/ProtectedRoute/utils';
import * as Sentry from '@sentry/react';
import { backendHost, LEAD_TRACKING_URL } from 'utils/constants';
import { getUTMData } from './cookies';
import {
  Event,
  IdentifyIntegrations,
  IdentifyTraits,
  IdentifyTraitsBase,
  TrackProperties,
} from '../types';

export const getUserCurrency = (country: string) => (country === 'US' ? 'USD' : 'GBP');

export const cTrackingListener = () => {
  if (checkWithinSeCe()) return; // NEVER use 3P scripts within SeCe.

  const capterraVkey = '1150b5fd130ced0149065a26555d213a';
  const capterraVid = '2096571';
  const ct = document.createElement('img');
  ct.src = `https://ct.capterra.com/capterra_tracker.gif?vid=${capterraVid}&vkey=${capterraVkey}`;
  document.body.appendChild(ct);
};

/**
 * Wrapper for the Segment identify call
 * @param traits Will be passed as traits
 * @param integrations Will be passed to the options object
 * @param skipPensieve If true, skips sending lead to Pensieve endpoint
 */
export function identifySegmentEvent(
  traits: IdentifyTraits,
  integrations: IdentifyIntegrations = {},
  skipPensieve: boolean = false,
) {
  // Send to Pensieve via monolith endpoint (dual-write during migration)
  if (!skipPensieve) {
    trackLeadToPensieve(traits);
  }

  // Keep existing Segment call
  if (typeof window.analytics !== 'undefined') {
    /**
     * Anatomy: analytics.identify([userId], [traits], [options], [callback]);
     * - userId, optional, string (can be ommited to use an anonymous id)
     * - traits, optional, object
     * - options, optional, object (if passing options with no traits, pass {} for traits)
     * - callback, optional, function
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/ajs-classic/#identify
     */
    window.analytics.identify(traits, {
      integrations: {
        'Salesforce (Actions)': integrations.salesforce ?? true,
      },
    });
  }
}

/**
 * Send lead tracking data to Pensieve via monolith endpoint
 * @param traits Lead data to track
 */
export async function trackLeadToPensieve(traits: IdentifyTraits): Promise<void> {
  if (!traits.email || !traits.country || !traits.firstName || !traits.lastName) {
    return;
  }

  const payload = {
    firstName: traits.firstName,
    lastName: traits.lastName,
    email: traits.email,
    country: traits.country,
    ...(traits.phone && { phone: traits.phone }),
    ...('utm_referrer' in traits && traits.utm_referrer && { referrer: traits.utm_referrer }),
    ...(traits.adobeECID && { ecid: traits.adobeECID }),
    ...(traits.signup_waiting_list && { signup_waiting_list: traits.signup_waiting_list }),
    ...Object.keys(traits)
      .filter((key) => key.startsWith('utm_'))
      .reduce((acc, key) => ({ ...acc, [key]: traits[key as keyof IdentifyTraits] }), {}),
  };

  try {
    await fetch(`${backendHost}${LEAD_TRACKING_URL}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: 'pensieve_lead_tracking' },
      extra: { email: traits.email },
    });
  }
}

/**
 * Wrapper for the Segment event tracking call
 * @param event Will be passed as the event key
 * @param properties Will be passed to the properties object
 */
export const trackSegmentEvent = (
  event: Event,
  properties: TrackProperties = {},
  integrations: IdentifyIntegrations = {},
) => {
  if (typeof window.analytics !== 'undefined') {
    window.analytics.track(event, properties, {
      integrations: {
        'Salesforce (Actions)': integrations.salesforce ?? true,
      },
    });
  }
};

export const addUTMData = (segmentData: IdentifyTraitsBase) => {
  const UTMData = getUTMData(); // exports needed to allow mocking
  return { ...segmentData, ...UTMData };
};

export const getMarketingConsent = (
  country: string | undefined,
  hasConsent: boolean | undefined,
) => {
  const currentDate = new Date().toISOString();

  if (country === 'US' || hasConsent) {
    return {
      hasConsent: true,
      consentDate: currentDate,
    };
  }

  return {
    hasConsent: false,
    consentDate: '',
  };
};
